import { Injectable } from '@angular/core';
import {
  Auth,
  Unsubscribe,
  signOut,
  User,
  signInWithPopup,
  GoogleAuthProvider,
  ParsedToken,
} from '@angular/fire/auth';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  take,
  throwError,
} from 'rxjs';

const roles = ['active', 'admin', 'accountant', 'developer', 'financial'];
type Role = (typeof roles)[number];

@Injectable({ providedIn: 'root' })
export class ApyreAuthService {
  private listenerUnsubscribe?: Unsubscribe;
  private pendingLogin$?: Observable<boolean>;
  private _authReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public authReady$: Observable<boolean> = this._authReady$
    .asObservable()
    .pipe(filter((r) => !!r));
  public get currentUser() {
    return this.auth.currentUser;
  }
  public get myId() {
    return this.auth.currentUser?.uid;
  }
  public get loggedIn() {
    return !!this.auth.currentUser;
  }
  public get displayName() {
    return this.currentUser?.displayName;
  }
  private _currentUser$: BehaviorSubject<User | null> =
    new BehaviorSubject<User | null>(null);
  public currentUser$: Observable<User | null> =
    this._currentUser$.asObservable();
  public claims$: Observable<ParsedToken> = this.currentUser$.pipe(
    mergeMap((u) => {
      if (!u) return of({});
      return u?.getIdTokenResult().then((result) => result.claims ?? {});
    }),
  );
  public myRoles$: Observable<string[]> = this.claims$.pipe(
    map((claims) => {
      return Object.entries(claims).reduce((acc: string[], [key, value]) => {
        if (roles.includes(key) && !!value) {
          acc.push(key);
        }
        return acc;
      }, []);
    }),
  );
  public hasRole(role: Role): Observable<boolean> {
    return this.myRoles$.pipe(map((roles) => roles.includes(role)));
  }

  public displayName$: Observable<string | undefined> = this.currentUser$.pipe(
    map((u) => u?.displayName ?? undefined),
  );

  /**
   * Latest user after the first persistence-backed auth state is known.
   * Guards and login use this instead of a sync `currentUser` snapshot that
   * can still be null while IndexedDB restore is in flight.
   */
  public settledUser$: Observable<User | null> = this.authReady$.pipe(
    switchMap(() => this.currentUser$.pipe(take(1))),
  );

  constructor(private auth: Auth) {
    this.listenerUnsubscribe = auth.onAuthStateChanged((state) => {
      this._currentUser$.next(state);
      if (!this._authReady$.value) this._authReady$.next(true);
    });
  }

  ngOnDestroy() {
    if (this.listenerUnsubscribe) {
      this.listenerUnsubscribe();
    }
  }
  login(): Observable<boolean> {
    if (this.pendingLogin$) return this.pendingLogin$;

    this.pendingLogin$ = this.settledUser$.pipe(
      take(1),
      switchMap((user) => {
        if (user || this.auth.currentUser) {
          return this.whenCurrentUser().pipe(map(() => true));
        }
        return from(this.openGooglePopup()).pipe(
          switchMap(() => this.whenCurrentUser().pipe(map(() => true))),
          catchError((err) => {
            const recovered = this.auth.currentUser ?? this._currentUser$.value;
            if (recovered) {
              this._currentUser$.next(recovered);
              return of(true);
            }
            return throwError(() => err);
          }),
        );
      }),
      finalize(() => {
        this.pendingLogin$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    return this.pendingLogin$;
  }

  private openGooglePopup() {
    return signInWithPopup(
      this.auth,
      new GoogleAuthProvider().setCustomParameters({
        prompt: 'select_account',
      }),
    );
  }

  private whenCurrentUser(): Observable<User> {
    const existing = this.auth.currentUser ?? this._currentUser$.value;
    if (existing) {
      this._currentUser$.next(existing);
      return of(existing);
    }
    return this.currentUser$.pipe(
      filter((u): u is User => !!u),
      take(1),
    );
  }
  logout(): Promise<void> {
    return signOut(this.auth);
  }
}
